import { useCallback, useEffect, useMemo, useRef } from 'react';
/**
 * Custom hook for scrolling to top when dependencies change
 *
 * @param options - Configuration options for the scroll behavior
 * @returns Object containing scrollToTop function and container ref
 */
export const useScrollToTop = (options = {}) => {
    const { dependencies = [], enabled = true, behavior = 'smooth' } = options;
    const containerRef = useRef(null);
    const isScrollingRef = useRef(false);
    const previousDepsRef = useRef([]);
    // Memoize dependencies to prevent unnecessary effect triggers
    const memoizedDeps = useMemo(() => dependencies, [dependencies]);
    // Helper function to find scrollable elements
    const findScrollableElements = useCallback(() => {
        const allElements = document.querySelectorAll('*');
        const scrollableElements = [];
        allElements.forEach((element) => {
            const style = window.getComputedStyle(element);
            const isScrollable = style.overflow === 'auto' ||
                style.overflow === 'scroll' ||
                style.overflowY === 'auto' ||
                style.overflowY === 'scroll';
            if (isScrollable && element.scrollTop > 0) {
                scrollableElements.push({
                    element,
                    scrollTop: element.scrollTop,
                });
            }
        });
        return scrollableElements;
    }, []);
    // Helper function to scroll element to top
    const scrollElementToTop = useCallback((element) => {
        try {
            element.scrollTo({
                top: 0,
                left: 0,
                behavior,
            });
        }
        catch (error) {
            // Silently handle scroll errors
        }
    }, [behavior]);
    // Main scroll to top function
    const scrollToTop = useCallback(() => {
        if (!enabled || isScrollingRef.current)
            return;
        // Prevent multiple simultaneous scroll operations
        isScrollingRef.current = true;
        // Always scroll the container ref if it exists
        if (containerRef.current) {
            // Always scroll window to top - this is the most reliable method
            try {
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior,
                });
                // Fallback to direct property assignment
                window.scrollY = 0;
                window.scrollX = 0;
            }
            catch (error) {
                // Fallback to direct property assignment
                window.scrollY = 0;
                window.scrollX = 0;
            }
        }
        // Find and scroll all scrollable elements
        const scrollableElements = findScrollableElements();
        scrollableElements.forEach(({ element }) => {
            scrollElementToTop(element);
        });
        // Reset the scrolling flag after a short delay
        setTimeout(() => {
            isScrollingRef.current = false;
        }, 100);
    }, [enabled, findScrollableElements, scrollElementToTop, behavior]);
    // Auto-scroll when dependencies change - with proper memoization
    useEffect(() => {
        if (!enabled || memoizedDeps.length === 0) {
            return undefined;
        }
        // Simple comparison - check if any dependency has changed
        const depsChanged = memoizedDeps.some((dep, index) => previousDepsRef.current[index] !== dep);
        if (depsChanged && !isScrollingRef.current) {
            // Update previous deps
            previousDepsRef.current = [...memoizedDeps];
            // Execute scroll immediately instead of using setTimeout
            try {
                scrollToTop();
            }
            catch (error) {
                // Silently handle scroll errors
            }
        }
        else if (depsChanged && isScrollingRef.current) {
            // Update previous deps even if no change (for next comparison)
            previousDepsRef.current = [...memoizedDeps];
        }
        return undefined;
    }, [memoizedDeps, enabled, scrollToTop]);
    return {
        scrollToTop,
        containerRef,
    };
};
//# sourceMappingURL=useScrollToTop.js.map